<main class="about">

  <!-- 1. Hero ------------------------------------------------------------ -->
  <section class="about-hero">
    <div class="about-hero-inner">
      <span class="about-eyebrow">
        <span class="about-eyebrow-tag">Driver-verified</span>
        Station status you can trust
      </span>
      <h1>EV charging,<br>made for <span class="about-accent">drivers.</span></h1>
      <p class="about-lede">
        Find the right station, plan trips with confidence, and connect with the charging community
        — all in one place.
      </p>
      <div class="about-ctas">
        <a routerLink="/" class="about-btn about-btn--primary">
          Explore the map
          <svg class="about-btn-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7"/>
          </svg>
        </a>
        <a href="about#how-it-works" class="about-btn about-btn--secondary">How it works</a>
      </div>
    </div>
  </section>

  <!-- 2. Stats ------------------------------------------------------------ -->
  <section class="about-stats">
    <div class="about-stats-inner">
      <div class="about-stat">
        <div class="about-stat-num">650k+</div>
        <div class="about-stat-label">Stations mapped</div>
      </div>
      <div class="about-stat">
        <div class="about-stat-num">4.8★</div>
        <div class="about-stat-label">Average rating</div>
      </div>
      <div class="about-stat">
        <div class="about-stat-num">30+</div>
        <div class="about-stat-label">Countries</div>
      </div>
    </div>
  </section>

  <!-- 3. Section intro + Benefit 1 ---------------------------------------- -->
  <section class="about-section" id="how-it-works">
    <div class="about-container">
      <div class="about-section-intro">
        <span class="about-section-eyebrow">A peek at the product</span>
        <h2>Built for how drivers actually charge.</h2>
      </div>

      <!-- Benefit 1 — Find stations -->
      <div class="about-benefit">
        <div class="about-benefit-copy">
          <h3>Find preferred EV stations near you.</h3>
          <p class="about-benefit-lede">
            A robust network of community-vetted information helps you skip broken chargers, busy
            plugs, and stations that don't match your vehicle.
          </p>
          <ul class="about-benefit-list">
            <li>Filter out low-quality charging stations</li>
            <li>Detailed plug-level information</li>
            <li>Live status updates from real drivers</li>
          </ul>
        </div>

        <!-- Stations product card -->
        <div class="about-station-card">

          <div class="about-card-head">
            <div class="about-card-head-icon about-card-head-icon--brand">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <div class="about-card-head-text">
              <div class="about-card-head-title">12 stations near you</div>
              <div class="about-card-head-sub">Within 5 mi · Mission Bay, SF</div>
            </div>
            <span class="about-live-pill">Live</span>
          </div>

          <div class="about-station-filters">
            <button class="about-filter-chip active">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="m13 2-3 7h4l-3 7"/>
              </svg>
              DC Fast
            </button>
            <button class="about-filter-chip">All plugs</button>
            <button class="about-filter-chip">Available</button>
            <button class="about-filter-chip">Tesla</button>
          </div>

          <div class="about-station-list">
            <div class="about-station-row about-station-row--selected">
              <div class="about-station-pin">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m13 2-3 7h4l-3 7"/>
                </svg>
              </div>
              <div class="about-station-info">
                <div class="about-station-name">Tesla Supercharger — Mission Bay</div>
                <div class="about-station-meta">0.8 mi · 8 of 12 free</div>
              </div>
              <span class="about-station-kw">250 kW</span>
            </div>
            <div class="about-station-row">
              <div class="about-station-pin">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m13 2-3 7h4l-3 7"/>
                </svg>
              </div>
              <div class="about-station-info">
                <div class="about-station-name">EVgo Fast Charger</div>
                <div class="about-station-meta">1.2 mi · 3 of 4 free</div>
              </div>
              <span class="about-station-kw">150 kW</span>
            </div>
            <div class="about-station-row about-station-row--busy">
              <div class="about-station-pin">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m13 2-3 7h4l-3 7"/>
                </svg>
              </div>
              <div class="about-station-info">
                <div class="about-station-name">Electrify America — 4th &amp; King</div>
                <div class="about-station-meta">1.6 mi · 1 of 6 free</div>
              </div>
              <span class="about-station-kw">350 kW</span>
            </div>
            <div class="about-station-row about-station-row--offline">
              <div class="about-station-pin">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m13 2-3 7h4l-3 7"/>
                </svg>
              </div>
              <div class="about-station-info">
                <div class="about-station-name">ChargePoint Station</div>
                <div class="about-station-meta">2.1 mi · all in use</div>
              </div>
              <span class="about-station-kw">62 kW</span>
            </div>
          </div>

          <div class="about-station-footer">
            <a href="#">View all 12 stations
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </a>
          </div>

        </div>
      </div>
    </div>
  </section>

  <!-- 4. Benefit 2 — Trip planner ----------------------------------------- -->
  <section class="about-section about-section--alt" id="trip-planner">
    <div class="about-container">
      <div class="about-benefit about-benefit--reverse">
        <div class="about-benefit-copy">
          <h3>Road trips without the range anxiety.</h3>
          <p class="about-benefit-lede">
            Trip Planner builds a charging route around your vehicle's actual range, your preferred
            amenities, and station busyness — not just distance.
          </p>
          <ul class="about-benefit-list">
            <li>Range estimates calibrated to your vehicle</li>
            <li>Stops near food, restrooms, and shopping</li>
            <li>Live station busyness baked into the route</li>
          </ul>
        </div>

        <!-- Trip-plan product card -->
        <div class="about-trip-card">

          <div class="about-card-head">
            <div class="about-card-head-icon about-card-head-icon--secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="6" cy="19" r="3"/><circle cx="18" cy="5" r="3"/><path d="M6 16V8a4 4 0 0 1 8 0v8a4 4 0 0 0 8 0"/>
              </svg>
            </div>
            <div class="about-card-head-text">
              <div class="about-card-head-title">Trip plan</div>
              <div class="about-card-head-sub">2 charging stops · 6h 24m</div>
            </div>
            <button class="about-trip-edit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit
            </button>
          </div>

          <div class="about-trip-summary-block">
            <div class="about-trip-route">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>
              </svg>
              <span>San Francisco</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
              <span>Los Angeles</span>
            </div>
            <div class="about-trip-stats">
              <span>384 mi</span>
              <span>6h 24m</span>
              <span>37 min charging</span>
            </div>
          </div>

          <div class="about-trip-stops">
            <div class="about-trip-stop">
              <div class="about-trip-stop-marker"><div class="about-trip-stop-dot"></div></div>
              <div class="about-trip-stop-info">
                <div class="about-trip-stop-label">Start</div>
                <div class="about-trip-stop-name">San Francisco, CA</div>
                <div class="about-trip-stop-meta">Battery: 92%</div>
              </div>
            </div>
            <div class="about-trip-stop about-trip-stop--charge">
              <div class="about-trip-stop-marker"><div class="about-trip-stop-dot"></div></div>
              <div class="about-trip-stop-info">
                <div class="about-trip-stop-label">Charging stop · 22 min</div>
                <div class="about-trip-stop-name">Supercharger — Harris Ranch</div>
                <div class="about-trip-stop-meta">8 of 40 stalls free · 250 kW</div>
                <span class="about-trip-stop-pill">+248 mi range</span>
              </div>
            </div>
            <div class="about-trip-stop about-trip-stop--charge">
              <div class="about-trip-stop-marker"><div class="about-trip-stop-dot"></div></div>
              <div class="about-trip-stop-info">
                <div class="about-trip-stop-label">Charging stop · 15 min</div>
                <div class="about-trip-stop-name">EVgo — Tejon Ranch</div>
                <div class="about-trip-stop-meta">2 of 4 stalls free · 150 kW</div>
                <span class="about-trip-stop-pill">+162 mi range</span>
              </div>
            </div>
            <div class="about-trip-stop">
              <div class="about-trip-stop-marker"><div class="about-trip-stop-dot"></div></div>
              <div class="about-trip-stop-info">
                <div class="about-trip-stop-label">Arrive</div>
                <div class="about-trip-stop-name">Los Angeles, CA</div>
                <div class="about-trip-stop-meta">Battery: 24%</div>
              </div>
            </div>
          </div>

          <div class="about-trip-footer">
            <span><span class="about-trip-footer-label">ETA</span> &nbsp;<span class="about-trip-footer-value">6:48 PM</span></span>
            <button class="about-trip-cta">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="3 11 22 2 13 21 11 13 3 11"/>
              </svg>
              Start navigation
            </button>
          </div>

        </div>
      </div>
    </div>
  </section>

  <!-- 5. Benefit 3 — Community chat --------------------------------------- -->
  <section class="about-section" id="community-chat">
    <div class="about-container">
      <div class="about-benefit">
        <div class="about-benefit-copy">
          <h3>Every station has a live community chat.</h3>
          <p class="about-benefit-lede">
            Ask a question. Get an answer from a driver already there. Real-time tips that no static
            database can match — broken plugs, throttled bays, free spots opening up in five minutes.
          </p>
          <ul class="about-benefit-list">
            <li>Live conversation per station, not a global feed</li>
            <li>Owners and operators jump in to fix issues fast</li>
            <li>GitCharger staff are visible when help is needed</li>
          </ul>
        </div>

        <!-- Chat product card -->
        <div class="about-chat-card">

          <div class="about-card-head">
            <div class="about-card-head-icon about-card-head-icon--brand">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="m13 2-3 7h4l-3 7"/>
              </svg>
            </div>
            <div class="about-card-head-text">
              <div class="about-card-head-title">Mission Bay SuperCharger</div>
              <div class="about-card-head-sub">Driver chat · 4 active</div>
            </div>
            <span class="about-live-pill">Live</span>
          </div>

          <div class="about-chat-feed">
            <div class="about-chat-msg">
              <div class="about-chat-av about-chat-av--1">N</div>
              <div class="about-chat-bub">
                <div class="about-chat-bub-meta">
                  <span class="about-chat-bub-name">Nina W.</span>
                  <span class="about-chat-bub-time">1:42 PM</span>
                </div>
                <div class="about-chat-bub-body">Anyone here right now? On my way and only have 30 min.</div>
              </div>
            </div>
            <div class="about-chat-msg">
              <div class="about-chat-av about-chat-av--2">P</div>
              <div class="about-chat-bub">
                <div class="about-chat-bub-meta">
                  <span class="about-chat-bub-name">Pete K.</span>
                  <span class="about-chat-bub-time">1:43 PM</span>
                </div>
                <div class="about-chat-bub-body">Pulling out in 5 — bay 4. Should be all yours.</div>
                <div class="about-chat-bub-likes">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  3
                </div>
              </div>
            </div>
            <div class="about-chat-msg">
              <div class="about-chat-av about-chat-av--3">D</div>
              <div class="about-chat-bub">
                <div class="about-chat-bub-meta">
                  <span class="about-chat-bub-name">Diego R.</span>
                  <span class="about-chat-bub-time">1:44 PM</span>
                </div>
                <div class="about-chat-bub-body">FYI bay 2 is throttling to 80kW today. Bay 4 is the fastest right now.</div>
                <div class="about-chat-bub-likes">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  5
                </div>
              </div>
            </div>
            <div class="about-chat-msg">
              <div class="about-chat-av about-chat-av--1">N</div>
              <div class="about-chat-bub">
                <div class="about-chat-bub-meta">
                  <span class="about-chat-bub-name">Nina W.</span>
                  <span class="about-chat-bub-time">1:46 PM</span>
                </div>
                <div class="about-chat-bub-body">Lifesavers, both of you 🙏 Heading to bay 4.</div>
                <div class="about-chat-bub-likes">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  2
                </div>
              </div>
            </div>
            <div class="about-chat-msg">
              <div class="about-chat-av about-chat-av--4">S</div>
              <div class="about-chat-bub">
                <div class="about-chat-bub-meta">
                  <span class="about-chat-bub-name">Sam J.</span>
                  <span class="about-chat-bub-time">1:51 PM</span>
                </div>
                <div class="about-chat-bub-body">Just arrived — bay 4 charging at 248 kW, no wait. Confirmed!</div>
              </div>
            </div>
          </div>

          <div class="about-chat-composer">
            <input class="about-chat-composer-input" placeholder="Share an update…" disabled>
            <button class="about-chat-composer-send" title="Send" type="button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

        </div>
      </div>
    </div>
  </section>

  <!-- 6. Ecosystem strip -------------------------------------------------- -->
  <section class="about-ecosystem">
    <div class="about-container about-ecosystem-inner">
      <span class="about-section-eyebrow about-section-eyebrow--invert">Beyond the driver app</span>
      <h2>The GitCharger Platform</h2>
      <p class="about-ecosystem-lede">
        More than just an app for drivers. We build for the people who run charging networks and the
        brands that want to reach EV drivers.
      </p>
      <div class="about-ecosystem-grid">
        <a routerLink="/contact" class="about-ecosystem-card">
          <span class="about-ecosystem-card-label">For operators</span>
          <span class="about-ecosystem-card-title">Operator Dashboard</span>
          <span class="about-ecosystem-card-desc">
            Manage your charger fleet, monitor uptime, run firmware updates, and handle billing —
            from one place.
          </span>
          <span class="about-ecosystem-card-more">
            Contact Us
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </span>
        </a>
        <a href="https://business.gitcharger.com" target="_blank" rel="noopener" class="about-ecosystem-card">
          <span class="about-ecosystem-card-label">For advertisers</span>
          <span class="about-ecosystem-card-title">GitCharger Ads</span>
          <span class="about-ecosystem-card-desc">
            Reach EV drivers exactly when they're charging. Geo-targeted campaigns with measurable
            results.
          </span>
          <span class="about-ecosystem-card-more">
            Open Ads
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 17 17 7M7 7h10v10"/>
            </svg>
          </span>
        </a>
        <a routerLink="/contact" class="about-ecosystem-card about-ecosystem-card--soon">
          <span class="about-ecosystem-card-label-row">
            <span class="about-ecosystem-card-label">For developers</span>
            <span class="about-ecosystem-card-soon-pill">Coming soon</span>
          </span>
          <span class="about-ecosystem-card-title">GitCharger Data</span>
          <span class="about-ecosystem-card-desc">
            Real-time station data, network analytics, and APIs. Build EV products on the same
            data layer GitCharger uses.
          </span>
          <span class="about-ecosystem-card-more">
            Get notified
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </span>
        </a>
      </div>
    </div>
  </section>

  <!-- 7. Final CTA -------------------------------------------------------- -->
  <section class="about-cta">
    <h2>Ready to charge<br>the <span class="about-accent">smart way?</span></h2>
    <p class="about-cta-lede">Free to use. No subscription. Just better information for the drive ahead.</p>
    <div class="about-ctas">
      <a routerLink="/" class="about-btn about-btn--primary">
        Explore the map
        <svg class="about-btn-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M13 5l7 7-7 7"/>
        </svg>
      </a>
      <a routerLink="/contact" class="about-btn about-btn--secondary">Get in touch</a>
    </div>
  </section>

</main>
